// Cookie utilities
const setCookie = (name, value, hours = 24) => {
    const date = new Date();
    date.setTime(date.getTime() + (hours * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
};

const getCookie = (name) => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length);
        if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length, cookie.length);
    }
    return null;
};

// Get UTM parameter from URL
const getUrlParameter = (name) => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name) || null;
};

// Get UTM value with priority: URL parameter -> Cookie -> null
const getUtmValue = (utmParam) => {
    const urlValue = getUrlParameter(utmParam);

    if (urlValue) {
        // Store in cookie for 24 hours
        setCookie(utmParam, urlValue, 24);
        return urlValue;
    }

    // Check cookie
    const cookieValue = getCookie(utmParam);
    return cookieValue || '';
};

// Export utility functions for use in other components
export { setCookie, getCookie, getUrlParameter, getUtmValue };

/**
 * Reusable form submission component with built-in security features:
 * - CSRF token handling
 * - Honeypot field validation
 * - Snaptcha validation
 * - Time-based submission validation
 * - Error and success state management
 */
export default function formComponent(options = {}) {
    const {
        endpoint = '/actions/scp/form/submit',
        formHandle = null,
        minSubmitTime = 3000, // minimum time before form can be submitted (milliseconds)
        honeypotFieldName = 'website',
        successMessage = 'Your submission has been received.<br>We\'ll be in touch soon.',
        errorMessage = 'An error occurred while submitting the form.',
        onSuccess = null,
        onError = null,
        beforeSubmit = null,
    } = options;

    return {
        // State properties
        formLoadedAt: Date.now(),
        isSubmitting: false,
        submitted: false,
        error: null,
        success: null,
        snaptchaReady: false,

        // Message configuration
        _successMessage: successMessage,
        _errorMessage: errorMessage,

        async init() {
            try {
                await this.refreshSnaptchaField(this.$el);
            } catch (error) {
                console.error('Unable to load spam protection:', error);
            }
        },

        /**
         * Main form submission handler
         * @param {Event} event - Form submit event
         * @param {Object} payload - Data to be submitted
         */
        async submit(event, payload = {}) {
            event.preventDefault();

            // Run beforeSubmit hook if provided
            if (beforeSubmit && typeof beforeSubmit === 'function') {
                const shouldContinue = await beforeSubmit(payload);
                if (shouldContinue === false) {
                    return;
                }
            }

            const form = event.target;

            // Security checks
            if (!this.validateHoneypot(form)) {
                console.warn('Bot detected: honeypot field filled');
                return;
            }

            if (!this.validateSubmitTime()) {
                console.warn('Bot detected: form submitted too quickly');
                return;
            }

            // Set submitting state
            this.isSubmitting = true;
            this.error = null;
            this.success = null;

            try {
                const result = await this.sendRequest(form, payload);

                if (result.success) {
                    this.submitted = true;
                    this.success = true;
                    this.error = null;

                    // Call success callback if provided
                    if (onSuccess && typeof onSuccess === 'function') {
                        onSuccess(result);
                    }
                } else {
                    console.error('Form submission failed');
                    console.error('Error:', result.error);
                    this.submitted = true;
                    this.error = result.error || this._errorMessage;
                    this.success = false;

                    // Call error callback if provided
                    if (onError && typeof onError === 'function') {
                        onError(result.error);
                    }
                }

                return result;
            } catch (error) {
                console.error('Error submitting form:', error);
                this.submitted = true;
                this.error = error.message || this._errorMessage;
                this.success = false;

                // Call error callback if provided
                if (onError && typeof onError === 'function') {
                    onError(error.message);
                }

                throw error;
            } finally {
                this.isSubmitting = false;
            }
        },

        /**
         * Send the actual HTTP request
         * @param {HTMLFormElement} form - The form element
         * @param {Object} payload - Data to be submitted
         */
        async sendRequest(form, payload) {
            const params = new URLSearchParams();

            // Add payload
            if (typeof payload === 'string') {
                params.append('payload', payload);
            } else if (typeof payload === 'object') {
                params.append('payload', JSON.stringify(payload));
            }

            let refreshedCsrfToken = null;
            try {
                refreshedCsrfToken = await this.refreshCsrfToken(form);
            } catch (error) {
                console.warn('Proceeding with existing CSRF token after refresh failure:', error);
            }

            // Add CSRF token
            const csrfInput = form.querySelector('input[name^="CRAFT_CSRF_TOKEN"]');
            if (csrfInput) {
                params.append(csrfInput.name, refreshedCsrfToken || csrfInput.value);
            }

            // Add honeypot field value
            const honeypot = form.querySelector(`input[name="${honeypotFieldName}"]`);
            if (honeypot) {
                params.append(honeypotFieldName, honeypot.value);
            }

            // Add the dynamically generated Snaptcha field.
            const snaptcha = form.querySelector('[data-snaptcha-field] input[name]');
            if (!snaptcha?.value) {
                throw new Error('Spam protection is still loading. Please try again.');
            }
            params.append(snaptcha.name, snaptcha.value);

            // Add form loaded timestamp
            params.append('form_loaded_at', this.formLoadedAt);

            if (formHandle) {
                params.append('form_handle', formHandle);
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                },
                body: params
            });

            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('We could not verify your submission. Please try again.');
            }

            return await response.json();
        },

        /**
         * Refresh CSRF token in the form
         * @param {HTMLFormElement} form - The form element
         */
        async refreshCsrfToken(form) {
            if (!form) {
                return null;
            }

            // Craft may render the CSRF param name as CRAFT_CSRF_TOKEN or a prefixed variant.
            const csrfInput = form.querySelector('input[name^="CRAFT_CSRF_TOKEN"]');
            if (!csrfInput) {
                return null;
            }

            const response = await fetch('/actions/blitz/csrf/token', {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'text/plain, */*'
                },
                cache: 'no-store',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`CSRF refresh failed (${response.status})`);
            }

            const token = (await response.text()).trim();
            if (token) {
                csrfInput.value = token;
            }

            return token;
        },

        /**
         * Fetch a unique Snaptcha field for this form.
         * @param {HTMLFormElement} form - The form element
         */
        async refreshSnaptchaField(form) {
            const container = form?.querySelector('[data-snaptcha-field]');
            if (!container) {
                return;
            }

            this.snaptchaReady = false;

            const response = await fetch('/actions/snaptcha/field/get-field', {
                headers: {
                    'Accept': 'application/json'
                },
                cache: 'no-store',
                credentials: 'same-origin'
            });

            if (!response.ok) {
                throw new Error(`Spam protection failed to load (${response.status})`);
            }

            const field = await response.json();
            if (!field.name || !field.value) {
                throw new Error('Spam protection returned an invalid field.');
            }

            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = field.name;
            input.value = field.value;
            input.autocomplete = 'off';
            container.replaceChildren(input);

            this.formLoadedAt = Date.now();
            this.snaptchaReady = true;
        },

        /**
         * Validate honeypot field (should be empty)
         * @param {HTMLFormElement} form - The form element
         */
        validateHoneypot(form) {
            const honeypot = form.querySelector(`input[name="${honeypotFieldName}"]`);
            return !honeypot || honeypot.value === '';
        },

        /**
         * Validate that form wasn't submitted too quickly
         */
        validateSubmitTime() {
            const timeSinceLoad = Date.now() - this.formLoadedAt;
            return timeSinceLoad >= minSubmitTime;
        },

        /**
         * Reset form state
         */
        reset() {
            this.isSubmitting = false;
            this.submitted = false;
            this.error = null;
            this.success = null;
            this.formLoadedAt = Date.now();
        },

        /**
         * Get the display message based on current state
         */
        displayMessage() {
            if (this.success) {
                return this._successMessage;
            }
            if (this.error) {
                return this.error;
            }
            return '';
        },

        /**
         * Get the display title based on current state
         */
        displayTitle() {
            if (this.success) {
                return 'Thank You!';
            }
            if (this.error) {
                return 'Error';
            }
            return '';
        }
    };
}
